import { useState } from 'react';
import { Card } from '../App';
import { ManaSymbol } from './ManaSymbol';
import { probabilityColoredSources, cardsSeenByTurn, probabilityLandsByTurn, formatProbability, getProbabilityColorClass } from '../lib/probability';

interface ColorDistributionChartProps {
    deck: Card[];
    onThePlay?: boolean;
    targetTurn?: number;
    onTargetTurnChange?: (turn: number) => void;
    minLands?: number;
    onMinLandsChange?: (lands: number) => void;
    handSize?: number;
    onHandSizeChange?: (size: number) => void;
    bottomedCardIds?: (string | null)[];
}

interface ColorData {
    key: string;
    name: string;
    pips: number;
    sources: number;
    needed: number;
    color: string;
    bgColor: string;
}

export function ColorDistributionChart({
    deck,
    onThePlay = true,
    targetTurn = 3,
    onTargetTurnChange,
    minLands = 3,
    onMinLandsChange,
    handSize = 7,
    onHandSizeChange,
    bottomedCardIds = []
}: ColorDistributionChartProps) {
    // Local state for controls if not controlled externally
    const [localTurn, setLocalTurn] = useState(targetTurn);
    const [localMinLands, setLocalMinLands] = useState(minLands);
    const [localHandSize, setLocalHandSize] = useState(handSize);

    const effectiveTurn = onTargetTurnChange ? targetTurn : localTurn;
    const effectiveMinLands = onMinLandsChange ? minLands : localMinLands;
    const effectiveHandSize = onHandSizeChange ? handSize : localHandSize;

    const handleTurnChange = (t: number) => {
        if (onTargetTurnChange) onTargetTurnChange(t);
        else setLocalTurn(t);
    };

    const handleMinLandsChange = (n: number) => {
        if (onMinLandsChange) onMinLandsChange(n);
        else setLocalMinLands(n);
    };

    const handleHandSizeChange = (h: number) => {
        if (onHandSizeChange) onHandSizeChange(h);
        else setLocalHandSize(h);
    };

    const sourceDivisor = onThePlay ? 2.5 : 2.3;

    const calculateColorData = (): ColorData[] => {
        const colorMap: Record<string, { pips: number; sources: number }> = {
            W: { pips: 0, sources: 0 },
            U: { pips: 0, sources: 0 },
            B: { pips: 0, sources: 0 },
            R: { pips: 0, sources: 0 },
            G: { pips: 0, sources: 0 },
        };

        deck.forEach(card => {
            if (card.manaCost) {
                const pips = card.manaCost.match(/{[WUBRG]}/g) || [];
                pips.forEach(pip => {
                    const color = pip[1] as keyof typeof colorMap;
                    if (colorMap[color]) {
                        colorMap[color].pips += card.quantity;
                    }
                });
                const hybrids = card.manaCost.match(/{([WUBRG])\/([WUBRG])}/g) || [];
                hybrids.forEach(hybrid => {
                    const c1 = hybrid[1] as keyof typeof colorMap;
                    const c2 = hybrid[3] as keyof typeof colorMap;
                    if (colorMap[c1]) colorMap[c1].pips += card.quantity * 0.5;
                    if (colorMap[c2]) colorMap[c2].pips += card.quantity * 0.5;
                });
            }
        });

        deck.forEach(card => {
            if (card.isLand && card.type) {
                const type = card.type.toLowerCase();
                const oracle = (card.oracleText || '').toLowerCase();

                if (type.includes('mountain') || oracle.includes('add {r}')) colorMap.R.sources += card.quantity;
                if (type.includes('plains') || oracle.includes('add {w}')) colorMap.W.sources += card.quantity;
                if (type.includes('island') || oracle.includes('add {u}')) colorMap.U.sources += card.quantity;
                if (type.includes('swamp') || oracle.includes('add {b}')) colorMap.B.sources += card.quantity;
                if (type.includes('forest') || oracle.includes('add {g}')) colorMap.G.sources += card.quantity;

                // 5-color lands count as 1 full source for each color
                if (oracle.includes('any color') || oracle.includes('one mana of any')) {
                    Object.keys(colorMap).forEach(c => {
                        colorMap[c].sources += card.quantity;
                    });
                }
            }
        });

        const colorConfig = [
            { key: 'W', name: 'White', color: '#F9E076', bgColor: 'rgba(249, 224, 118, 0.3)' },
            { key: 'U', name: 'Blue', color: '#0E86D4', bgColor: 'rgba(14, 134, 212, 0.3)' },
            { key: 'B', name: 'Black', color: '#9B8E9E', bgColor: 'rgba(155, 142, 158, 0.3)' },
            { key: 'R', name: 'Red', color: '#E05D44', bgColor: 'rgba(224, 93, 68, 0.3)' },
            { key: 'G', name: 'Green', color: '#00A86B', bgColor: 'rgba(0, 168, 107, 0.3)' },
        ];

        return colorConfig.map(c => ({
            ...c,
            pips: colorMap[c.key].pips,
            sources: Math.round(colorMap[c.key].sources),
            needed: Math.ceil(colorMap[c.key].pips / sourceDivisor),
        }));
    };

    const colorData = calculateColorData();
    const allColors = colorData;
    const hasAnyColor = allColors.some(c => c.pips > 0 || c.sources > 0);

    if (!hasAnyColor) {
        return (
            <div className="text-center text-white/40 py-4 text-sm">
                No colored mana requirements
            </div>
        );
    }

    const totalPips = Math.max(1, allColors.reduce((sum, c) => sum + c.pips, 0));
    const totalSources = Math.max(1, allColors.reduce((sum, c) => sum + c.sources, 0));

    const size = 130;
    const center = size / 2;
    const maxRadius = size / 2 - 20;

    const angleStep = (2 * Math.PI) / 5;
    const startAngle = -Math.PI / 2;

    const getPoint = (index: number, value: number, max: number) => {
        const angle = startAngle + index * angleStep;
        const radius = max > 0 ? (value / max) * maxRadius : 0;
        return {
            x: center + radius * Math.cos(angle),
            y: center + radius * Math.sin(angle),
        };
    };

    const maxValue = Math.max(
        ...allColors.map(c => Math.max(
            totalPips > 0 ? c.pips / totalPips : 0,
            totalSources > 0 ? c.sources / totalSources : 0
        ))
    ) || 1;

    const idealPath = allColors
        .map((c, i) => {
            const ratio = totalPips > 0 ? c.pips / totalPips : 0;
            const point = getPoint(i, ratio, maxValue);
            return `${point.x},${point.y}`;
        })
        .join(' ');

    const actualPath = allColors
        .map((c, i) => {
            const ratio = totalSources > 0 ? c.sources / totalSources : 0;
            const point = getPoint(i, ratio, maxValue);
            return `${point.x},${point.y}`;
        })
        .join(' ');

    const gridLevels = [0.25, 0.5, 0.75, 1];
    const activeColors = allColors.filter(c => c.pips > 0 || c.sources > 0);

    return (
        <div className="flex flex-col items-center">
            {/* Radar Chart Container */}
            <div className="relative overflow-visible" style={{ width: size + 60, height: size + 60 }}>
                {/* Mana pip icons around chart */}
                {allColors.map((c, i) => {
                    const angle = startAngle + i * angleStep;
                    const labelX = (size / 2) + 30 + (maxRadius + 18) * Math.cos(angle);
                    const labelY = (size / 2) + 30 + (maxRadius + 18) * Math.sin(angle);
                    return (
                        <div
                            key={c.key}
                            className="absolute flex items-center justify-center"
                            style={{ left: labelX - 10, top: labelY - 10, width: 20, height: 20 }}
                        >
                            <ManaSymbol symbol={c.key} size="sm" />
                        </div>
                    );
                })}

                <svg width={size} height={size} className="absolute" style={{ left: 30, top: 30 }}>
                    {/* Definitions for gradients and filters */}
                    <defs>
                        {/* Glow filter for ideal polygon */}
                        <filter id="idealGlow" x="-50%" y="-50%" width="200%" height="200%">
                            <feGaussianBlur stdDeviation="3" result="coloredBlur" />
                            <feMerge>
                                <feMergeNode in="coloredBlur" />
                                <feMergeNode in="SourceGraphic" />
                            </feMerge>
                        </filter>
                        {/* Glow filter for actual polygon */}
                        <filter id="actualGlow" x="-50%" y="-50%" width="200%" height="200%">
                            <feGaussianBlur stdDeviation="4" result="coloredBlur" />
                            <feMerge>
                                <feMergeNode in="coloredBlur" />
                                <feMergeNode in="SourceGraphic" />
                            </feMerge>
                        </filter>
                        {/* Radial gradient for ideal */}
                        <radialGradient id="idealGradient" cx="50%" cy="50%" r="70%">
                            <stop offset="0%" stopColor="rgba(251, 191, 36, 0.4)" />
                            <stop offset="100%" stopColor="rgba(251, 191, 36, 0.05)" />
                        </radialGradient>
                        {/* Radial gradient for actual */}
                        <radialGradient id="actualGradient" cx="50%" cy="50%" r="70%">
                            <stop offset="0%" stopColor="rgba(168, 85, 247, 0.5)" />
                            <stop offset="100%" stopColor="rgba(168, 85, 247, 0.1)" />
                        </radialGradient>
                        {/* Drop shadow */}
                        <filter id="shadow" x="-20%" y="-20%" width="140%" height="140%">
                            <feDropShadow dx="0" dy="2" stdDeviation="2" floodColor="rgba(0,0,0,0.5)" />
                        </filter>
                    </defs>

                    {/* Grid circles with subtle gradient */}
                    {gridLevels.map(level => (
                        <circle
                            key={level}
                            cx={center}
                            cy={center}
                            r={maxRadius * level}
                            fill="none"
                            stroke={`rgba(255,255,255,${0.05 + level * 0.08})`}
                            strokeWidth="1"
                        />
                    ))}

                    {/* Axis lines */}
                    {allColors.map((c, i) => {
                        const angle = startAngle + i * angleStep;
                        const endX = center + maxRadius * Math.cos(angle);
                        const endY = center + maxRadius * Math.sin(angle);
                        return <line key={c.key} x1={center} y1={center} x2={endX} y2={endY} stroke="rgba(255,255,255,0.12)" strokeWidth="1" />;
                    })}

                    {/* Ideal polygon (dashed, with glow) */}
                    <polygon
                        points={idealPath}
                        fill="url(#idealGradient)"
                        stroke="rgb(251, 191, 36)"
                        strokeWidth="2"
                        strokeDasharray="6,3"
                        filter="url(#idealGlow)"
                        opacity="0.9"
                    />

                    {/* Actual polygon (solid, with glow and shadow) */}
                    <polygon
                        points={actualPath}
                        fill="url(#actualGradient)"
                        stroke="rgb(168, 85, 247)"
                        strokeWidth="2.5"
                        filter="url(#actualGlow)"
                    />

                    {/* Data points with glow */}
                    {allColors.map((c, i) => {
                        const ratio = totalSources > 0 ? c.sources / totalSources : 0;
                        const point = getPoint(i, ratio, maxValue);
                        return (
                            <g key={c.key}>
                                <circle cx={point.x} cy={point.y} r="6" fill={c.color} opacity="0.3" />
                                <circle cx={point.x} cy={point.y} r="4" fill={c.color} stroke="white" strokeWidth="1.5" />
                            </g>
                        );
                    })}
                </svg>
            </div>

            {/* Legend row - Ideal vs Actual */}
            <div className="flex items-center justify-center gap-4 text-[9px] mb-1">
                <div className="flex items-center gap-1">
                    <div className="w-3 h-0.5 bg-amber-400 border-dashed" />
                    <span className="text-amber-400">Ideal</span>
                </div>
                <div className="flex items-center gap-1">
                    <div className="w-3 h-0.5 bg-purple-400" />
                    <span className="text-purple-400">Actual</span>
                </div>
            </div>

            {/* Meters with integrated stats */}
            <div className="flex flex-col gap-0 w-full" style={{ maxWidth: size + 60 }}>
                {activeColors.map(c => {
                    const diff = c.sources - c.needed;
                    const status = diff >= 2 ? 'excellent' : diff >= 0 ? 'good' : diff >= -2 ? 'close' : 'deficit';
                    const statusClass = {
                        excellent: 'text-green-400',
                        good: 'text-green-400/70',
                        close: 'text-yellow-400',
                        deficit: 'text-red-400',
                    }[status];

                    // Calculate probability of having at least 1 source of this color by target turn
                    // Account for bottomed cards
                    const totalBottomed = bottomedCardIds.filter(id => id !== null).length;
                    const deckSize = (deck.reduce((sum, card) => sum + card.quantity, 0) || 60) - totalBottomed;

                    // Count how many sources of this color are bottomed
                    let bottomedSources = 0;
                    const colorKey = c.key.toLowerCase();
                    const colorNames: Record<string, string> = { w: 'plains', u: 'island', b: 'swamp', r: 'mountain', g: 'forest' };

                    bottomedCardIds.forEach(cardId => {
                        if (cardId) {
                            const card = deck.find(d => d.id === cardId);
                            if (card?.isLand) {
                                const type = (card.type || '').toLowerCase();
                                const oracle = (card.oracleText || '').toLowerCase();

                                // Check basic land types
                                if (type.includes(colorNames[colorKey] || '')) {
                                    bottomedSources++;
                                }
                                // Check oracle text for "add {color}"
                                else if (oracle.includes(`add {${colorKey}}`)) {
                                    bottomedSources++;
                                }
                                // Check for 5-color lands
                                else if (oracle.includes('any color') || oracle.includes('one mana of any')) {
                                    bottomedSources++;
                                }
                            }
                        }
                    });

                    const effectiveSources = Math.max(0, Math.round(c.sources) - bottomedSources);
                    const cardsSeen = cardsSeenByTurn(effectiveTurn, onThePlay, 0, effectiveHandSize);
                    // Need at least 1 source to cast anything of this color
                    const prob = probabilityColoredSources(deckSize, effectiveSources, 1, cardsSeen);
                    const probPercent = Math.round(prob * 100);


                    return (
                        <div key={c.key} className="flex items-center gap-1.5 text-[9px]">
                            <div className="w-4 h-4 flex-shrink-0 flex items-center justify-center">
                                <ManaSymbol symbol={c.key} size="sm" />
                            </div>
                            <span className="text-white/40 w-4 flex-shrink-0 text-right">{c.pips}p</span>
                            <div className="flex-1 min-w-0 h-1 bg-white/10 rounded-full overflow-hidden">
                                <div
                                    className="h-full rounded-full"
                                    style={{
                                        width: `${(c.sources / Math.max(c.sources, c.needed, 1)) * 100}%`,
                                        backgroundColor: c.color,
                                    }}
                                />
                            </div>
                            <span className={`${statusClass} w-8 flex-shrink-0 text-right`}>{c.sources}/{c.needed}</span>
                            <span className={`w-8 flex-shrink-0 text-right font-mono ${probPercent >= 80 ? 'text-green-400' : probPercent >= 60 ? 'text-yellow-400' : 'text-red-400'}`}>
                                {probPercent}%
                            </span>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

export default ColorDistributionChart;
